import { apiRequest } from "../util/apiUtils";

/**
 * Etsy channel calls.
 *
 * These are the only API calls in the app made without a session: an Etsy buyer
 * arrives with no account and no email, because Etsy never releases a buyer's
 * address. The order number plus one shipping detail is what stands in for a
 * login, and a successful claim returns the session token everything else uses.
 */

/** Where the resume token lives between visits. */
export const ETSY_RESUME_TOKEN_KEY = "etsy_resume_token";

/**
 * The claimed Etsy order number, kept for the length of the session.
 *
 * This is what tells the rest of the app "this person came from Etsy" — which
 * matters on the last wizard step, where an Etsy buyer must be offered "send to
 * print" rather than a checkout they have already paid for.
 *
 * It is a convenience marker, not a permission: every Etsy endpoint verifies
 * ownership against the session on the server, so a forged value here buys
 * nothing.
 */
export const ETSY_RECEIPT_ID_KEY = "etsy_receipt_id";

/**
 * Claim an order.
 *
 * The API answers 200 for both success and failure, with `ok` telling them
 * apart — failures deliberately carry no detail about which field was wrong,
 * so that guessing an order number is not a way to discover which orders exist.
 */
export const claimEtsyOrder = async ({ orderNumber, name, zip, email, marketingOptIn }) => {
  try {
    const res = await apiRequest("post", `etsy/claim`, {
      body: { orderNumber, name, zip, email, marketingOptIn },
    });
    return res?.data;
  } catch (err) {
    // A network or 5xx failure is not a failed claim; say so plainly rather
    // than telling the buyer their details were wrong.
    return {
      ok: false,
      networkError: true,
      message: "We couldn't reach the server. Please check your connection and try again.",
    };
  }
};

/** Resume a previously claimed order from the stored token. */
export const resumeEtsyClaim = async (token) => {
  try {
    const res = await apiRequest("post", `etsy/claim/resume`, { body: { token } });
    return res?.data;
  } catch (err) {
    return { ok: false, networkError: true };
  }
};

/**
 * Tell the API the book is finished and may be printed.
 *
 * `bookId` is what links the finished book to the Etsy order — the order was
 * created at claim time, before any book existed. The API verifies the book
 * belongs to this session before joining them.
 */
export const submitEtsyOrder = async (receiptId, bookId) => {
  try {
    const res = await apiRequest("post", `etsy/submit`, { body: { receiptId, bookId } });
    return res?.data;
  } catch (err) {
    return {
      ok: false,
      networkError: true,
      message: "We couldn't reach the server. Please try again.",
    };
  }
};

/**
 * Remember this browser so the buyer does not retype their details.
 *
 * Wrapped because storage throws in private mode and with site data blocked,
 * and a failure here must not break an otherwise successful claim — it only
 * costs the buyer a retype next time.
 */
export const storeResumeToken = (token) => {
  if (!token) return;
  try {
    localStorage.setItem(ETSY_RESUME_TOKEN_KEY, token);
  } catch (err) {
    /* not fatal: the buyer can claim again with their order number */
  }
};

export const readResumeToken = () => {
  try {
    return localStorage.getItem(ETSY_RESUME_TOKEN_KEY);
  } catch (err) {
    return null;
  }
};

export const clearResumeToken = () => {
  try {
    localStorage.removeItem(ETSY_RESUME_TOKEN_KEY);
  } catch (err) {
    /* nothing to do */
  }
};

/** Remember which Etsy order this session is building for. */
export const storeEtsyReceiptId = (receiptId) => {
  if (!receiptId) return;
  try {
    localStorage.setItem(ETSY_RECEIPT_ID_KEY, String(receiptId));
  } catch (err) {
    /* not fatal: the buyer just sees the normal checkout button */
  }
};

/** The Etsy order this session is building for, or null for a Shopify customer. */
export const readEtsyReceiptId = () => {
  try {
    return localStorage.getItem(ETSY_RECEIPT_ID_KEY);
  } catch (err) {
    return null;
  }
};

export const clearEtsyReceiptId = () => {
  try {
    localStorage.removeItem(ETSY_RECEIPT_ID_KEY);
  } catch (err) {
    /* nothing to do */
  }
};

/** Is this session building a book for an Etsy order? */
export const isEtsySession = () => !!readEtsyReceiptId();

/**
 * Is the LOGGED-IN user an Etsy buyer? Decided from identity, not storage.
 *
 * Every Etsy buyer's account uses a synthetic address on the reserved
 * `etsy.invalid` domain (the API assigns it; Etsy never releases a real one).
 * That makes the email a reliable channel marker that comes from the server
 * with the session and cannot outlive it.
 *
 * `isEtsySession()` above reads a localStorage flag, which is fine for the
 * claim page's own bookkeeping but WRONG for deciding what a shared screen
 * shows: the flag is written on claim and only cleared on submit, so on a
 * shared browser it can still be present when a Shopify customer signs in —
 * and would swap their checkout button for "send to print". Screens shared
 * with Shopify customers must use this, keyed on the actual user.
 */
export const ETSY_SYNTHETIC_EMAIL_DOMAIN = "etsy.invalid";

export const isEtsyUser = (profile) => {
  const email = profile && typeof profile.email === "string" ? profile.email : "";
  return email.trim().toLowerCase().endsWith(`@${ETSY_SYNTHETIC_EMAIL_DOMAIN}`);
};

/** Drop every Etsy marker. Called when a non-Etsy session starts. */
export const clearEtsyMarkers = () => {
  clearEtsyReceiptId();
  clearResumeToken();
  clearEtsyFinished();
};

/**
 * "This tab finished an Etsy build." Set the instant the buyer submits, before
 * the session is torn down, so that pressing Back into the wizard afterwards
 * can be answered with "this page is no longer available" instead of the
 * Shopify sign-in the buyer has never seen. sessionStorage on purpose: it is
 * per tab and dies with it, which is exactly the lifetime of "the build you
 * just finished".
 */
export const ETSY_FINISHED_KEY = "etsy_finished";

export const markEtsyFinished = () => {
  try {
    sessionStorage.setItem(ETSY_FINISHED_KEY, "1");
  } catch (_) {
    /* private mode etc. — the Back trap on the done page still applies */
  }
};

export const isEtsyFinished = () => {
  try {
    return sessionStorage.getItem(ETSY_FINISHED_KEY) === "1";
  } catch (_) {
    return false;
  }
};

export const clearEtsyFinished = () => {
  try {
    sessionStorage.removeItem(ETSY_FINISHED_KEY);
  } catch (_) {
    /* nothing to clear */
  }
};

/**
 * End the Etsy buyer's session and show the confirmation.
 *
 * Used when the build is over: right after the buyer sends their book to print,
 * and when they come back through /etsy for an order that was already sent. The
 * session has no further purpose either way, so it is ended rather than left
 * dangling.
 *
 * Why a full page load rather than navigate(): the app has two redirects that
 * both fire on an in-page transition and both belong to the Shopify flow —
 * a signed-in user on a public page is sent back into their wizard, and a
 * session that turns unauthorized on a protected page is sent to sign-in
 * (which forwards to the Shopify store). Clearing storage and then booting
 * fresh at /etsy/done sidesteps both: the new page load is simply a
 * logged-out visitor on a public route, so neither redirect has anything to
 * act on, and no shared routing code has to change for Etsy's sake.
 */
export const finishEtsySession = ({ format = "printed", copies = 1 } = {}) => {
  // Before the session goes: lets a later Back press into the wizard be
  // answered with "no longer available" rather than the sign-in page.
  markEtsyFinished();
  clearEtsyReceiptId();
  clearResumeToken();
  localStorage.removeItem("auth_token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("role");
  // The session is gone once the page loads, so the confirmation cannot ask
  // the API what was bought; the wording for anything but a plain printed
  // book rides on the URL (the format, and how many printed copies when more
  // than one).
  const params = new URLSearchParams();
  if (format === "digital" || format === "combined") params.set("format", format);
  const n = Number.parseInt(copies, 10);
  if (format !== "digital" && n > 1) params.set("copies", String(n));
  const query = params.toString();
  window.location.assign(query ? `/etsy/done?${query}` : "/etsy/done");
};
